import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { ViewModeEnum } from '@shared/models/consts';
import { LocalSettingsQuery } from '@shared/store/localSettings/local-settings-query';
import { LocalSettingsStore } from '@shared/store/localSettings/local-settings-store';
import { HostPlatformService } from '@shared/services/host-platform.service';

const expirationTimeMS = 23 * 60 * 60 * 1000; // 23 hours

@Injectable({
	providedIn: 'root'
})
export class IntuitiveFlowService {
	private readonly preferOldRxModeSubject$ = new BehaviorSubject<boolean>(false);
	readonly preferOldRxMode$ = this.preferOldRxModeSubject$.asObservable();

	/** Intuitive UI only for procedure-flow (V1) with NewFlowRx; V0 always uses classic Rx. */
	readonly isIntuitiveFlow$: Observable<boolean> = defer(() =>
		combineLatest([
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.NewFlowRx),
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.FallbackToOldRxIsAvailable),
			this.shellQuery.isProcedureFlow$,
			this.preferOldRxMode$,
			this.hostPlatformService.isScanner$,
			this.hostPlatformService.isMidc$
		]).pipe(
			map(([hasNewFlowRx, hasFallbackIsAvailable, isProcedureFlow, preferOldRx, isScanner, isMidc]) => {
				if (!isScanner && !isMidc) {
					return false;
				}
				if (hasFallbackIsAvailable) {
					return hasNewFlowRx && isProcedureFlow && !preferOldRx;
				}

				return hasNewFlowRx && isProcedureFlow;
			})
		)
	);

	readonly isSummaryMode$: Observable<boolean> = defer(() =>
		combineLatest([
			this.shellQuery.isProcedureFlow$,
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.SummaryPage),
			this.shellQuery.viewMode$
		]).pipe(
			map(
				([isProcedureFlow, hasSummaryPageSwo, viewMode]) =>
					isProcedureFlow && hasSummaryPageSwo && viewMode === ViewModeEnum.Summary
			)
		)
	);

	readonly isFallbackBannerAvailable$ = defer(() =>
		combineLatest([
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.NewFlowRx),
			this.softwareOptionsService.hasCompanySoftwareOption$(SoftwareOptions.FallbackToOldRxIsAvailable),
			this.shellQuery.isProcedureFlow$,
			this.shellQuery.isNewAndNotClonedRx$,
			this.hostPlatformService.isScanner$,
			this.hostPlatformService.isMidc$
		]).pipe(
			map(([hasNewFlowRxSwo, hasFallbackIsAvailable, isProcedureFlow, isNewAndNotClonedRx, isScanner, isMidc]) => {
				if (!isScanner && !isMidc) {
					return false;
				}

				return hasNewFlowRxSwo && isProcedureFlow && hasFallbackIsAvailable && isNewAndNotClonedRx;
			})
		)
	);

	get preferOldRxMode() {
		return this.preferOldRxModeSubject$.getValue();
	}

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly localSettingsQuery: LocalSettingsQuery,
		private readonly localSettingsStore: LocalSettingsStore,
		private readonly hostPlatformService: HostPlatformService,
	) {}

	initPreferOldRxMode(): void {
		const contactId = this.shellQuery.contactId;

		if (contactId == null) {
			return;
		}

		const settings = this.localSettingsQuery.userSettings[contactId];

		const preferOldRx =
			!!settings?.preferOldRxMode &&
			!!settings?.preferOldRxModeSetAt &&
			Date.now() - settings.preferOldRxModeSetAt < expirationTimeMS;

		this.preferOldRxModeSubject$.next(preferOldRx);
	}

	setRxModePreference(preferredNewRx: boolean): void {
		const contactId = this.shellQuery.contactId;

		if (contactId == null) {
			return;
		}

		if (preferredNewRx) {
			this.localSettingsStore.updateLocalSettings(contactId, {
				preferOldRxMode: undefined,
				preferOldRxModeSetAt: undefined
			});
		} else {
			this.localSettingsStore.updateLocalSettings(contactId, {
				preferOldRxMode: true,
				preferOldRxModeSetAt: Date.now()
			});
		}

		this.preferOldRxModeSubject$.next(!preferredNewRx);
	}

	cleanExpiredRxModePreference(): void {
		const allUserSettings = this.localSettingsQuery.userSettings;

		Object.keys(allUserSettings).forEach(key => {
			const id = Number(key);
			const settings = allUserSettings[id];

			if (
				settings?.preferOldRxMode &&
				settings?.preferOldRxModeSetAt != null &&
				Date.now() - settings.preferOldRxModeSetAt >= expirationTimeMS
			) {
				this.localSettingsStore.updateLocalSettings(id, {
					preferOldRxMode: undefined,
					preferOldRxModeSetAt: undefined
				});
			}
		});
	}
}
